/* ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THIS FILE IS A COPY. THE ORIGINAL LIVES IN THE WEB REPO:
 *     HypeTunes_Web_2026/src/components/three/clipRetarget.js
 *
 * Plays a clip authored on one rig on any body (the shared clip library,
 * services/clipLibrary.js). One implementation, shared like faceRig/faceDecal.
 *
 * ⚠ DO NOT EDIT BELOW THE SENTINEL. Fix the Web original, then:
 *     node scripts/check_face_decal_sync.mjs --sync
 * ───────────────────────────────────────────────────────────────────────────── */

/* ── VERBATIM FROM HypeTunes_Web_2026 BELOW THIS LINE ── */
import * as THREE from 'three'

/**
 * ONE CLIP, EVERY BODY — play an animation authored on one rig on any other.
 *
 * Every body comes out of its own rig run, and the rigger re-rolls the rest pose
 * each time: the same bone can rest 115–173° apart between two bodies (measured
 * 2026-10-28, RightUpLeg). Played as-is, a borrowed clip fuses the legs and
 * throws the arms; that is why clips used to be "only valid on the rig run they
 * came from", and why adding one meant re-rigging the body.
 *
 * The fix is to play the clip's CHANGE from its own rest on top of the target's
 * rest, per bone:
 *
 *     target(t) = targetRest · sourceRest⁻¹ · source(t)
 *
 * The source rest travels inside every clip GLB (its node table), so a clip
 * needs nothing but its own file. Proven by eye on four bodies with clips from
 * five rigs (API docs/3d_body_renewal/FINDINGS.md §10).
 *
 * ⚠ CAPTURE THE TARGET REST BEFORE ANY CLIP TOUCHES A BONE. The mixer rewrites
 * bones every frame, so a rest read later is whatever was playing.
 * ⚠ HIPS PLAY IN PLACE: x/z are pinned to the target's rest (rigger clips carry
 * root motion in centimetres from wherever the animator stood) and only the
 * vertical moves, shifted by the difference in hip height between the rigs.
 * ⚠ What it cannot fix is a body's own skinning: a mesh built with its arms
 * welded to its sides tears on an arm raise however the clip arrives.
 */

/** Local rest transform of every bone under `root`. Call BEFORE the mixer runs. */
export function captureRest(root) {
  const rest = new Map()
  root?.traverse((o) => { if (o.isBone) rest.set(o.name, { q: o.quaternion.clone(), p: o.position.clone() }) })
  return rest
}

/**
 * @param {THREE.AnimationClip} clip  the clip as loaded (left untouched; a clone is returned)
 * @param {THREE.Object3D} sourceRoot the clip GLB's scene — its nodes ARE the source rig's rest
 * @param {Map} targetRest            captureRest(body), taken before any clip played
 * @param {{ pinXZ?: boolean }} opts
 * @returns {{ clip: THREE.AnimationClip, maxRestGapDeg: number, worstBone: string }}
 */
export function retargetClip(clip, sourceRoot, targetRest, { pinXZ = true } = {}) {
  const out = clip.clone()
  const srcRest = new Map()
  sourceRoot?.traverse((o) => srcRest.set(o.name, { q: o.quaternion.clone(), p: o.position.clone() }))
  out.tracks = out.tracks.filter((t) => !t.name.endsWith('.scale'))
  const corr = new THREE.Quaternion(); const q = new THREE.Quaternion()
  let maxRestGapDeg = 0; let worstBone = ''
  for (const tr of out.tracks) {
    const { nodeName, propertyName } = THREE.PropertyBinding.parseTrackName(tr.name)
    const t = targetRest.get(nodeName); const s = srcRest.get(nodeName)
    if (!t || !s) continue
    if (propertyName === 'quaternion') {
      const gap = 2 * Math.acos(Math.min(1, Math.abs(t.q.dot(s.q)))) * 180 / Math.PI
      if (gap > maxRestGapDeg) { maxRestGapDeg = gap; worstBone = nodeName }
      corr.copy(t.q).multiply(s.q.clone().invert())
      const v = tr.values
      for (let i = 0; i < v.length; i += 4) q.fromArray(v, i).premultiply(corr).toArray(v, i)
    } else if (propertyName === 'position') {
      const v = tr.values
      for (let i = 0; i < v.length; i += 3) {
        if (pinXZ) { v[i] = t.p.x; v[i + 2] = t.p.z }
        v[i + 1] += t.p.y - s.p.y
      }
    }
  }
  return { clip: out, maxRestGapDeg, worstBone }
}

export default retargetClip
